/**
 * 🚗 Autosell — 2 pestañas:
 *  - Pipeline: kanban de tus vehículos
 *  - Oportunidades: chollos detectados 24/7 por scouts (Coches.net, Wallapop, Milanuncios...)
 */
import { h, apiClient, formatEur, pill, emptyState, btn, openModal, openConfirm, toast, sectionHeader, statTile, openQuickTaskModal } from './_common.js';
import { renderTasksForSection } from './tareas.js';

const STATUS_COLORS = {
  sourcing: 'gray', inspecting: 'blue', available: 'gold', reserved: 'gold', sold: 'green', withdrawn: 'red',
};
const STATUS_LABELS = {
  sourcing: 'Sourcing', inspecting: 'Inspección', available: 'Disponible', reserved: 'Reservado', sold: 'Vendido', withdrawn: 'Retirado',
};

let _autoTab = 'pipeline';   // 'pipeline' | 'opportunities' | 'web-leads'
let _opportFilters = { source: '', minScore: 0, status: '' };
let _leadsRefreshTimer = null;
let _leadsAgoTimer = null;
let _lastLeadsTs = null;

function stopLeadsTimers() {
  if (_leadsRefreshTimer) { clearInterval(_leadsRefreshTimer); _leadsRefreshTimer = null; }
  if (_leadsAgoTimer) { clearInterval(_leadsAgoTimer); _leadsAgoTimer = null; }
}

function leadsAgo(iso) {
  if (!iso) return '—';
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 5) return 'ahora mismo';
  if (diff < 60) return `hace ${diff}s`;
  if (diff < 3600) return `hace ${Math.floor(diff/60)} min`;
  if (diff < 86400) return `hace ${Math.floor(diff/3600)}h`;
  return `hace ${Math.floor(diff/86400)}d`;
}

const LEAD_STATUS_COLORS = { nuevo: 'gold', contactado: 'blue', cualificado: 'green', cerrado: 'green', descartado: 'gray' };
const LEAD_STATUS_LABELS = { nuevo: '🆕 Nuevo', contactado: '📞 Contactado', cualificado: '✅ Cualificado', cerrado: '💰 Cerrado', descartado: '❌ Descartado' };

/* ============================================================
 * META ADS · TARJETAS EN DIRECTO (resumen integrado en Autosell)
 * VERSION 2 (2026-05-31 16:30): innerHTML directo, sin h() helpers.
 * A prueba de balas — las cards SIEMPRE se renderizan aunque
 * fetch falle. Luego rellena con datos del endpoint Meta.
 * ============================================================ */
function renderMetaAdsBar(container) {
  // Cards SIEMPRE visibles (con "—" si no hay datos)
  container.innerHTML = `
    <div class="mb-5 mt-1" data-meta-bar-v2="1">
      <div class="flex items-center justify-between mb-2">
        <h3 class="text-sm font-semibold text-ink-200 flex items-center gap-2">
          <span class="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
          📣 META ADS · EN DIRECTO <span class="text-xs text-ink-400 font-normal">(cuenta Autosell)</span>
        </h3>
        <span class="text-xs text-ink-400" id="autosell-meta-status">cargando…</span>
      </div>
      <div class="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div class="bg-gradient-to-br from-amber-500/20 to-amber-500/5 border border-amber-400/40 rounded-xl p-3">
          <div class="text-[10px] uppercase tracking-wider text-amber-300 font-bold mb-1">💸 Gasto HOY</div>
          <div class="text-2xl font-display font-bold text-amber-200" id="autosell-meta-spend-today">—</div>
          <div class="text-[10px] text-ink-400 mt-1" id="autosell-meta-spend-yesterday">—</div>
        </div>
        <div class="bg-gradient-to-br from-sky-500/20 to-sky-500/5 border border-sky-400/40 rounded-xl p-3">
          <div class="text-[10px] uppercase tracking-wider text-sky-300 font-bold mb-1">📊 Gasto 7d</div>
          <div class="text-2xl font-display font-bold text-sky-200" id="autosell-meta-spend-7d">—</div>
          <div class="text-[10px] text-ink-400 mt-1" id="autosell-meta-spend-30d">—</div>
        </div>
        <div class="bg-gradient-to-br from-emerald-500/20 to-emerald-500/5 border border-emerald-400/40 rounded-xl p-3">
          <div class="text-[10px] uppercase tracking-wider text-emerald-300 font-bold mb-1">👁 Alcance 7d</div>
          <div class="text-2xl font-display font-bold text-emerald-200" id="autosell-meta-reach-7d">—</div>
          <div class="text-[10px] text-ink-400 mt-1" id="autosell-meta-impressions">—</div>
        </div>
        <div class="bg-gradient-to-br from-pink-500/20 to-pink-500/5 border border-pink-400/40 rounded-xl p-3">
          <div class="text-[10px] uppercase tracking-wider text-pink-300 font-bold mb-1">🖱 Clicks 7d</div>
          <div class="text-2xl font-display font-bold text-pink-200" id="autosell-meta-clicks-7d">—</div>
          <div class="text-[10px] text-ink-400 mt-1" id="autosell-meta-ctr">—</div>
        </div>
        <div class="bg-gradient-to-br from-violet-500/20 to-violet-500/5 border border-violet-400/40 rounded-xl p-3">
          <div class="text-[10px] uppercase tracking-wider text-violet-300 font-bold mb-1">📺 Anuncios</div>
          <div class="text-2xl font-display font-bold text-violet-200" id="autosell-meta-active-ads">—</div>
          <div class="text-[10px] text-ink-400 mt-1" id="autosell-meta-status-ads">—</div>
        </div>
      </div>
    </div>
  `;

  // Fetch en background (no bloquea el render)
  const fmt = (n) => {
    const v = Number(n);
    if (!Number.isFinite(v) || v === 0) return '0,00 €';
    return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(v);
  };
  const num = (n) => new Intl.NumberFormat('es-ES').format(Number(n) || 0);
  const set = (id, val) => { const el = document.getElementById(id); if (el) el.textContent = val; };

  fetch('/api/v1/meta-ads/live', { credentials: 'include' })
    .then((r) => r.ok ? r.json() : Promise.reject(new Error('HTTP ' + r.status)))
    .then((d) => {
      if (!d || d.error) {
        set('autosell-meta-status', d?.error ? '⚠️ Meta no configurada' : '⚠️ sin datos');
        return;
      }
      const s = d.summary || {};
      set('autosell-meta-spend-today', fmt(s.spendToday));
      set('autosell-meta-spend-yesterday', `Ayer: ${fmt(s.spendYesterday)}`);
      set('autosell-meta-spend-7d', fmt(s.spend7d));
      set('autosell-meta-spend-30d', `Mes (30d): ${fmt(s.spend30d)}`);
      set('autosell-meta-reach-7d', num(s.reach7d || s.reach));
      set('autosell-meta-impressions', `${num(s.impressionsToday || s.impressions)} impresiones hoy`);
      set('autosell-meta-clicks-7d', num(s.clicks7d || s.clicks));
      const ctr = s.ctr7d || s.ctr;
      set('autosell-meta-ctr', ctr ? `CTR ${Number(ctr).toFixed(2)}%` : '—');
      set('autosell-meta-active-ads', String(s.activeAds || 0) + ' activos');
      set('autosell-meta-status-ads', `${s.pausedAds || 0} pausados · ${s.inReviewAds || 0} en revisión`);
      set('autosell-meta-status', '✅ actualizado · refresh auto cada 5 min');
    })
    .catch((e) => {
      set('autosell-meta-status', `⚠️ ${e?.message || 'error'}`);
    });

  // Refresh cada 5 min
  setTimeout(() => {
    if (document.body.contains(container)) renderMetaAdsBar(container);
  }, 5 * 60 * 1000);
}

export async function renderAutosell(root) {
  stopLeadsTimers();
  root.innerHTML = '';
  const wrap = h('div', { class: 'animate-fade-in' });
  wrap.appendChild(sectionHeader('🚗', 'Autosell', 'Pipeline + Oportunidades 24/7', [
    btn('✅ + Tarea', () => openQuickTaskModal('autosell'), { variant: 'secondary' }),
    btn('🤖 Lanzar Scouts', () => runVehicleScouts(root)),
    btn('+ Añadir vehículo', () => openVehicleModal(null, () => renderAutosell(root))),
    btn('Admin Autosell.es', () => window.open('https://autosell.es/admin', '_blank'), { variant: 'secondary' }),
  ]));

  // === MÉTRICAS META ADS EN DIRECTO (cards arriba) ===
  // NO sustituye la sección Meta Ads completa — solo añade un resumen aquí.
  const metaBar = h('div', { id: 'autosell-meta-bar' });
  wrap.appendChild(metaBar);
  renderMetaAdsBar(metaBar);

  // Tabs
  wrap.appendChild(h('div', { class: 'flex gap-1 border-b border-base-700 mb-5' }, [
    autosellTab('pipeline', '🚗 Pipeline', root),
    autosellTab('opportunities', '💎 Oportunidades 24/7', root),
    autosellTab('web-leads', '📥 Leads Web (en directo)', root),
    autosellTab('tasks', '✅ Tareas', root),
  ]));
  wrap.appendChild(h('div', { id: 'autosell-tab-content' }));
  root.appendChild(wrap);

  // Quick links — cuentas/plataformas de vehículos
  try {
    const { renderQuickLinks } = await import('./_quick-links.js');
    const qlContainer = h('div');
    wrap.insertBefore(qlContainer, wrap.querySelector('#autosell-tab-content'));
    await renderQuickLinks(qlContainer, 'autosell', {
      title: '🔗 Mis cuentas Autosell',
      subtitle: 'Coches.net, Wallapop, Milanuncios, AutoScout24, Mobile.de — acceso directo.',
      onReload: () => renderAutosell(root),
    });
  } catch (e) { /* skip */ }

  if (_autoTab === 'pipeline') {
    await renderPipelineTab(wrap, root);
  } else if (_autoTab === 'opportunities') {
    await renderOpportunitiesTab(wrap, root);
  } else if (_autoTab === 'web-leads') {
    await renderWebLeadsTab(wrap, root);
  } else if (_autoTab === 'tasks') {
    const taskContainer = h('div', { class: 'bg-base-800/30 rounded-xl p-5 border border-base-700' });
    wrap.querySelector('#autosell-tab-content')?.appendChild(taskContainer);
    if (!wrap.querySelector('#autosell-tab-content')) wrap.appendChild(taskContainer);
    await renderTasksForSection(taskContainer, 'autosell');
  }
}

/* ============================================================
 * TAB · Leads del formulario web autosell.es (en directo)
 * Auto-refresh cada 30s · usa /api/v1/autosell/web-leads
 * ============================================================ */
async function renderWebLeadsTab(wrap, root) {
  const container = wrap.querySelector('#autosell-tab-content');
  container.innerHTML = '';

  // Header con indicador live
  const tsLabel = h('span', { class: 'text-xs text-ink-300', id: 'autosell-leads-ago' }, 'Cargando...');
  container.appendChild(h('div', { class: 'flex items-center justify-between mb-3' }, [
    h('div', { class: 'flex items-center gap-2 text-sm' }, [
      h('span', { class: 'inline-flex items-center gap-1.5' }, [
        h('span', { class: 'w-2 h-2 rounded-full bg-emerald-400 animate-pulse' }),
        h('span', { class: 'text-emerald-300 font-semibold' }, 'EN DIRECTO'),
      ]),
      h('span', { class: 'text-ink-400' }, '·'),
      h('span', {}, 'auto-refresh 30s ·'),
      tsLabel,
    ]),
    btn('🔄 Refrescar ahora', () => refreshWebLeads(container, root)),
  ]));

  // Contenedor del contenido
  const content = h('div', { id: 'autosell-leads-content' });
  container.appendChild(content);

  // Primera carga
  await refreshWebLeads(container, root);

  // Auto-refresh cada 30s
  _leadsRefreshTimer = setInterval(() => refreshWebLeads(container, root), 30000);
  _leadsAgoTimer = setInterval(() => {
    const el = document.getElementById('autosell-leads-ago');
    if (el && _lastLeadsTs) el.textContent = `Última actualización: ${leadsAgo(_lastLeadsTs)}`;
  }, 10000);
}

async function refreshWebLeads(container, root) {
  const content = container.querySelector('#autosell-leads-content');
  if (!content) return;
  try {
    const data = await apiClient.get('/api/v1/autosell/web-leads?limit=100');
    _lastLeadsTs = data.lastUpdated || new Date().toISOString();
    const el = document.getElementById('autosell-leads-ago');
    if (el) el.textContent = `Última actualización: ${leadsAgo(_lastLeadsTs)}`;
    content.innerHTML = '';

    const s = data.summary || {};
    // Stats bar
    content.appendChild(h('div', { class: 'grid grid-cols-2 md:grid-cols-4 gap-3 mb-4' }, [
      statTile({ label: 'Total leads', value: s.total || 0, emoji: '📊', color: 'gold' }),
      statTile({ label: 'Hoy', value: s.hoy || 0, emoji: '🆕', color: 'green' }),
      statTile({ label: 'Esta semana', value: s.semana || 0, emoji: '📅', color: 'blue' }),
      statTile({ label: 'Por contactar', value: s.nuevos || 0, emoji: '📞', color: 'red' }),
    ]));

    const leads = data.leads || [];
    if (leads.length === 0) {
      content.appendChild(emptyState('📥',
        'Sin leads del formulario web todavía',
        'Cuando alguien rellene el form en autosell.es aparecerán aquí instantáneamente. La página se refresca sola cada 30s.',
        h('a', { href: 'https://autosell.es', target: '_blank', rel: 'noopener', class: 'inline-block bg-gold-400 hover:bg-gold-300 text-base-900 px-4 py-2 rounded-lg text-sm font-semibold' }, '→ Ver formulario en autosell.es')
      ));
      return;
    }

    // Tabla de leads
    content.appendChild(h('h3', { class: 'font-display font-semibold mb-2 mt-2' }, `📥 Mensajes recibidos (${leads.length})`));
    const table = h('div', { class: 'bg-base-800/50 border border-base-700 rounded-xl overflow-hidden' }, [
      h('div', { class: 'overflow-x-auto' }, [
        h('table', { class: 'w-full text-sm' }, [
          h('thead', { class: 'bg-base-700/50' }, [
            h('tr', {}, [
              h('th', { class: 'text-left p-3' }, 'Recibido'),
              h('th', { class: 'text-left p-3' }, 'Cliente'),
              h('th', { class: 'text-left p-3' }, 'Vehículo'),
              h('th', { class: 'text-left p-3' }, 'Detalles'),
              h('th', { class: 'text-left p-3' }, 'Estado'),
              h('th', { class: 'text-center p-3' }, 'Acción'),
            ]),
          ]),
          h('tbody', {}, leads.map((l) => leadRow(l, root, container))),
        ]),
      ]),
    ]);
    content.appendChild(table);
  } catch (e) {
    content.innerHTML = '';
    content.appendChild(emptyState('⚠️', 'Error cargando leads', e?.message || 'Reintenta', btn('Reintentar', () => refreshWebLeads(container, root))));
  }
}

function leadRow(l, root, container) {
  const status = l.status || 'nuevo';
  const recienteHoy = l.created_at && new Date(l.created_at) > new Date(Date.now() - 24*60*60*1000);
  return h('tr', {
    class: `border-t border-base-700 hover:bg-base-700/30 cursor-pointer ${recienteHoy ? 'bg-emerald-500/5' : ''}`,
    onclick: (e) => {
      // Si clica en un link o botón de acciones, no abrir modal
      const tag = (e.target.tagName || '').toLowerCase();
      if (tag === 'a' || tag === 'button' || e.target.closest('a') || e.target.closest('button')) return;
      openLeadDetailModal(l, container, root);
    },
  }, [
    h('td', { class: 'p-3 whitespace-nowrap' }, [
      h('div', { class: 'text-xs text-ink-100' }, leadsAgo(l.created_at)),
      l.fuente ? h('div', { class: 'text-[10px] text-ink-400 mt-0.5' }, `📥 ${l.fuente}`) : null,
    ]),
    h('td', { class: 'p-3' }, [
      h('div', { class: 'font-semibold' }, l.nombre || '—'),
      l.telefono ? h('a', { href: `tel:${l.telefono}`, class: 'text-xs text-gold-300 hover:underline' }, `📞 ${l.telefono}`) : null,
    ]),
    h('td', { class: 'p-3' }, [
      h('div', { class: 'font-medium' }, [l.marca_modelo || '—', l.anio ? ` · ${l.anio}` : ''].join('')),
      h('div', { class: 'text-xs text-ink-300' }, [
        l.kilometros ? `${l.kilometros} km` : null,
        l.carburante,
        l.vehiculo_tipo,
      ].filter(Boolean).join(' · ')),
    ]),
    h('td', { class: 'p-3 max-w-xs' }, [
      l.estado_vehiculo ? h('div', { class: 'text-xs' }, `🔧 ${l.estado_vehiculo}`) : null,
      l.comentario ? h('div', { class: 'text-xs text-ink-300 italic mt-0.5 truncate', title: l.comentario }, `"${l.comentario}"`) : null,
      h('div', { class: 'text-[10px] text-sky-400 mt-1' }, 'Click para ver completo →'),
    ]),
    h('td', { class: 'p-3' }, pill(LEAD_STATUS_LABELS[status] || status, LEAD_STATUS_COLORS[status] || 'gray')),
    h('td', { class: 'p-3 text-center' }, [
      h('div', { class: 'flex gap-1 justify-center' }, [
        l.telefono ? h('a', {
          href: `tel:${l.telefono}`,
          class: 'inline-flex items-center justify-center w-8 h-8 rounded bg-emerald-500/15 hover:bg-emerald-500/30 text-emerald-300 text-sm',
          title: 'Llamar',
        }, '📞') : null,
        l.telefono ? h('a', {
          href: `https://wa.me/${l.telefono.replace(/\D/g,'')}`, target: '_blank', rel: 'noopener',
          class: 'inline-flex items-center justify-center w-8 h-8 rounded bg-green-600/15 hover:bg-green-600/30 text-green-300 text-sm',
          title: 'WhatsApp',
        }, '💬') : null,
        h('button', {
          class: 'inline-flex items-center justify-center w-8 h-8 rounded bg-gold-500/15 hover:bg-gold-500/30 text-gold-300 text-sm',
          title: 'Ver detalles y editar',
          onclick: (e) => { e.stopPropagation(); openLeadDetailModal(l, container, root); },
        }, '✏️'),
      ]),
    ]),
  ]);
}

/* ============================================================
 * MODAL · Detalle COMPLETO del lead + edición
 * Muestra TODOS los campos del form web sin truncar nada.
 * ============================================================ */
function openLeadDetailModal(lead, container, root) {
  // Parsear el comentario para extraer ATTR / UTM si los tiene en formato "txt [ATTR] utm_source=fb | ..."
  let comentarioLimpio = lead.comentario || '';
  let attribution = '';
  const attrMatch = (lead.comentario || '').match(/\[ATTR\](.+)$/s);
  if (attrMatch) {
    comentarioLimpio = (lead.comentario || '').split('[ATTR]')[0].trim();
    attribution = attrMatch[1].trim();
  }

  const fmt = (v) => v == null || v === '' ? '—' : v;
  const fechaRecibido = lead.created_at ? new Date(lead.created_at).toLocaleString('es-ES') : '—';
  const fechaUpdated = lead.updated_at ? new Date(lead.updated_at).toLocaleString('es-ES') : '—';

  // Construir HTML del detalle (read-only) para mostrar en modal
  const detailHtml = `
    <div style="background:#1a1a1f;border:1px solid #2a2a31;border-radius:12px;padding:18px;margin-bottom:18px;">
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:14px;margin-bottom:14px;">
        <div>
          <div style="font-size:10px;color:#6b7280;text-transform:uppercase;letter-spacing:1px;font-weight:600;margin-bottom:4px">📞 Cliente</div>
          <div style="font-size:16px;font-weight:700;color:#fff">${fmt(lead.nombre)}</div>
          <a href="tel:${lead.telefono || ''}" style="color:#fbbf24;font-size:13px;text-decoration:none">${fmt(lead.telefono)}</a>
          ${lead.email ? `<div style="font-size:12px;color:#9ca3af;margin-top:2px">${lead.email}</div>` : ''}
        </div>
        <div>
          <div style="font-size:10px;color:#6b7280;text-transform:uppercase;letter-spacing:1px;font-weight:600;margin-bottom:4px">📅 Recibido</div>
          <div style="font-size:13px;color:#fff">${fechaRecibido}</div>
          <div style="font-size:11px;color:#6b7280;margin-top:2px">${leadsAgo(lead.created_at)}</div>
          ${lead.fuente ? `<div style="font-size:11px;color:#86efac;margin-top:4px">📥 Fuente: <strong>${lead.fuente}</strong></div>` : ''}
        </div>
      </div>

      <div style="border-top:1px solid #2a2a31;padding-top:14px;margin-top:14px">
        <div style="font-size:10px;color:#6b7280;text-transform:uppercase;letter-spacing:1px;font-weight:600;margin-bottom:8px">🚗 Vehículo</div>
        <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:10px;font-size:13px">
          <div><span style="color:#6b7280">Tipo:</span> <strong style="color:#fff">${fmt(lead.vehiculo_tipo)}</strong></div>
          <div><span style="color:#6b7280">Marca/Modelo:</span> <strong style="color:#fff">${fmt(lead.marca_modelo)}</strong></div>
          <div><span style="color:#6b7280">Año:</span> <strong style="color:#fff">${fmt(lead.anio)}</strong></div>
          <div><span style="color:#6b7280">Kilómetros:</span> <strong style="color:#fff">${fmt(lead.kilometros)}</strong></div>
          <div><span style="color:#6b7280">Carburante:</span> <strong style="color:#fff">${fmt(lead.carburante)}</strong></div>
          <div><span style="color:#6b7280">Estado:</span> <strong style="color:#fff">${fmt(lead.estado_vehiculo)}</strong></div>
        </div>
      </div>

      ${comentarioLimpio ? `
      <div style="border-top:1px solid #2a2a31;padding-top:14px;margin-top:14px">
        <div style="font-size:10px;color:#6b7280;text-transform:uppercase;letter-spacing:1px;font-weight:600;margin-bottom:6px">💬 Comentario del cliente</div>
        <div style="background:#0f0f12;border-left:3px solid #fbbf24;padding:10px 12px;border-radius:6px;font-size:13px;color:#e8e8ea;font-style:italic;white-space:pre-wrap">${comentarioLimpio}</div>
      </div>
      ` : ''}

      ${attribution ? `
      <div style="border-top:1px solid #2a2a31;padding-top:14px;margin-top:14px">
        <div style="font-size:10px;color:#6b7280;text-transform:uppercase;letter-spacing:1px;font-weight:600;margin-bottom:6px">🎯 Atribución / UTM (de dónde vino)</div>
        <div style="background:#0f0f12;border-left:3px solid #60a5fa;padding:10px 12px;border-radius:6px;font-size:11px;color:#9ca3af;font-family:monospace;white-space:pre-wrap;word-break:break-all">${attribution}</div>
      </div>
      ` : ''}

      <div style="display:flex;gap:8px;margin-top:18px;flex-wrap:wrap">
        ${lead.telefono ? `<a href="tel:${lead.telefono}" style="flex:1;min-width:140px;background:#22c55e;color:#fff;text-decoration:none;padding:11px 14px;border-radius:8px;text-align:center;font-weight:700;font-size:13px">📞 LLAMAR AHORA</a>` : ''}
        ${lead.telefono ? `<a href="https://wa.me/${(lead.telefono || '').replace(/\D/g,'')}" target="_blank" rel="noopener" style="flex:1;min-width:140px;background:#25d366;color:#fff;text-decoration:none;padding:11px 14px;border-radius:8px;text-align:center;font-weight:700;font-size:13px">💬 WhatsApp</a>` : ''}
      </div>

      <div style="font-size:10px;color:#6b7280;margin-top:12px;text-align:center">
        ID: ${lead.id} · Última actualización: ${fechaUpdated}
      </div>
    </div>
  `;

  openModal({
    title: `📥 Lead · ${lead.nombre || 'Sin nombre'}`,
    subtitle: `${lead.marca_modelo || ''} ${lead.anio || ''}`.trim() || lead.vehiculo_tipo || 'Detalle completo',
    icon: '📥',
    size: 'lg',
    fields: [
      { name: '__detail_html', label: '', type: 'html', value: detailHtml },
      { name: 'status', label: 'Estado del lead', type: 'select', value: lead.status || 'nuevo', options: [
        { value: 'nuevo', label: '🆕 Nuevo' },
        { value: 'contactado', label: '📞 Contactado' },
        { value: 'cualificado', label: '✅ Cualificado (interesa)' },
        { value: 'cerrado', label: '💰 Cerrado (vendido)' },
        { value: 'descartado', label: '❌ Descartado' },
      ] },
      { name: 'precio_ofertado', label: '💵 Precio que le ofrecí (€)', type: 'number', value: lead.precio_ofertado, placeholder: '1500' },
      { name: 'precio_pagado', label: '💰 Precio pagado final (€)', type: 'number', value: lead.precio_pagado, placeholder: 'Solo si cerrado' },
      { name: 'notas_internas', label: '📝 Mis notas internas', type: 'textarea', rows: 5, value: lead.notas_internas,
        placeholder: 'Llamé a las 18:00, no contesta. Probar mañana 10:00. Pidió 2.000€, ofrecí 1.500€...' },
    ],
    submitLabel: 'Guardar cambios',
    onSubmit: async (vals) => {
      // No enviar el campo HTML de solo lectura
      const { __detail_html, ...payload } = vals;
      payload.precio_ofertado = payload.precio_ofertado ? Number(payload.precio_ofertado) : null;
      payload.precio_pagado = payload.precio_pagado ? Number(payload.precio_pagado) : null;
      await apiClient.patch(`/api/v1/autosell/web-leads/${lead.id}`, payload);
      toast('Lead actualizado ✓', 'success');
      refreshWebLeads(container, root);
    },
  });
}

// Función vieja del anterior parche — sustituida por openLeadDetailModal (modal mejorado con vista completa)

function autosellTab(key, label, root) {
  const active = _autoTab === key;
  return h('button', {
    class: `px-4 py-2 text-sm font-medium border-b-2 transition ${active ? 'border-gold-400 text-gold-300' : 'border-transparent text-ink-300 hover:text-ink-100'}`,
    onclick: () => { _autoTab = key; renderAutosell(root); },
  }, label);
}

async function renderPipelineTab(wrap, root) {
  const container = wrap.querySelector('#autosell-tab-content');
  container.innerHTML = '';

  const statsBar = h('div', { class: 'grid grid-cols-2 md:grid-cols-4 gap-3 mb-5' });
  container.appendChild(statsBar);

  const res = await apiClient.get('/api/v1/vehicles').catch(() => ({ data: [] }));
  const data = res.data || [];
  const total = data.length;
  const available = data.filter((v) => v.status === 'available').length;
  const sold = data.filter((v) => v.status === 'sold').length;
  const totalValue = data.reduce((s, v) => s + (v.askingPrice || 0), 0);
  statsBar.appendChild(statTile({ label: 'Vehículos', value: total, emoji: '🚗', color: 'gold' }));
  statsBar.appendChild(statTile({ label: 'Disponibles', value: available, emoji: '✓', color: 'green' }));
  statsBar.appendChild(statTile({ label: 'Vendidos', value: sold, emoji: '💰', color: 'blue' }));
  statsBar.appendChild(statTile({ label: 'Valor pipeline', value: formatEur(totalValue), emoji: '💎', color: 'purple' }));

  if (data.length === 0) {
    container.appendChild(emptyState('🚗', 'Pipeline vacío', 'Añade tu primer vehículo con el botón + arriba.', btn('+ Añadir vehículo', () => openVehicleModal(null, () => renderAutosell(root)))));
    return;
  }
  const grouped = {};
  for (const v of data) (grouped[v.status] = grouped[v.status] || []).push(v);

  const kanban = h('div', { class: 'grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3 overflow-x-auto' });
  for (const status of ['sourcing', 'inspecting', 'available', 'reserved', 'sold', 'withdrawn']) {
    const col = h('div', { class: 'bg-base-800/30 border border-base-700 rounded-xl p-3 min-w-[200px]' }, [
      h('div', { class: 'flex items-center justify-between mb-3' }, [
        h('h4', { class: 'text-xs uppercase tracking-widest text-ink-300 font-semibold' }, STATUS_LABELS[status] || status),
        pill(`${(grouped[status] || []).length}`, STATUS_COLORS[status] || 'gray'),
      ]),
      ...(grouped[status] || []).map((v) => vehCard(v, root)),
    ]);
    kanban.appendChild(col);
  }
  container.appendChild(kanban);
}

async function renderOpportunitiesTab(wrap, root) {
  const container = wrap.querySelector('#autosell-tab-content');
  container.innerHTML = '';

  // Stats
  const stats = await apiClient.get('/api/v1/autosell/opportunities/stats').catch(() => ({ totals: {}, bySource: {} }));
  const t = stats.totals || {};
  const statsBar = h('div', { class: 'grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-5' }, [
    statTile({ label: 'Total', value: t.total || 0, emoji: '💎', color: 'gold' }),
    statTile({ label: 'Hot deals', value: t.hotDeals || 0, emoji: '🔥', color: 'red' }),
    statTile({ label: 'Score medio', value: t.avgScore || 0, emoji: '⭐', color: 'gold' }),
    statTile({ label: 'Nuevas', value: t.new || 0, emoji: '🆕', color: 'blue' }),
    statTile({ label: 'Margen pot.', value: formatEur(t.potentialMarginEur || 0), emoji: '💰', color: 'green' }),
    statTile({ label: 'Destacadas', value: t.starred || 0, emoji: '⭐', color: 'purple' }),
  ]);
  container.appendChild(statsBar);

  // Filtros
  container.appendChild(h('div', { class: 'flex gap-2 mb-4 flex-wrap items-center' }, [
    h('select', {
      class: 'bg-base-800 border border-base-700 rounded-lg px-3 py-1.5 text-sm',
      onchange: (e) => { _opportFilters.source = e.target.value; renderAutosell(root); },
    }, [
      h('option', { value: '' }, 'Todas las fuentes'),
      h('option', { value: 'coches.net' }, '🚗 Coches.net'),
      h('option', { value: 'wallapop' }, '🛍️ Wallapop'),
      h('option', { value: 'milanuncios' }, '📰 Milanuncios'),
      h('option', { value: 'autoscout24' }, '🇩🇪 AutoScout24'),
      h('option', { value: 'mobile.de' }, '🇩🇪 Mobile.de'),
    ]),
    h('label', { class: 'text-xs text-ink-300 flex items-center gap-2' }, [
      'Score min:',
      h('input', {
        type: 'range', min: '0', max: '100', value: _opportFilters.minScore || 0, class: 'w-24',
        oninput: (e) => { _opportFilters.minScore = Number(e.target.value); document.getElementById('vop-score').textContent = e.target.value; },
        onchange: () => renderAutosell(root),
      }),
      h('span', { id: 'vop-score', class: 'text-gold-300 font-mono w-6' }, String(_opportFilters.minScore || 0)),
    ]),
    h('button', {
      class: 'ml-auto text-xs text-emerald-400',
      onclick: () => runVehicleScouts(root),
    }, '🔄 Lanzar Scouts ahora'),
  ]));

  // Banner info 24/7
  container.appendChild(h('div', { class: 'bg-emerald-500/10 border border-emerald-400/30 rounded-lg p-3 mb-4 flex items-center gap-3' }, [
    h('span', { class: 'text-2xl' }, '🤖'),
    h('div', { class: 'flex-1' }, [
      h('div', { class: 'font-semibold text-emerald-300' }, 'Scouts 24/7 activos'),
      h('div', { class: 'text-xs text-ink-300' }, 'Cron cada 4h analiza Coches.net, Wallapop, Milanuncios, AutoScout24, Mobile.de buscando vehículos con margen ≥ 1500€ y score ≥ 80. Las oportunidades aparecen automáticamente aquí.'),
    ]),
  ]));

  // Lista de oportunidades
  const res = await apiClient.get('/api/v1/autosell/opportunities').catch(() => ({ data: [] }));
  let opps = res.data || [];
  if (_opportFilters.source) opps = opps.filter((o) => o.source === _opportFilters.source);
  if (_opportFilters.minScore) opps = opps.filter((o) => (o.opportunityScore || 0) >= _opportFilters.minScore);

  if (opps.length === 0) {
    container.appendChild(emptyState('🔍', 'Sin oportunidades todavía', 'Los scouts se ejecutan cada 4h. Pulsa "Lanzar Scouts ahora" para un análisis inmediato.', btn('🤖 Lanzar Scouts', () => runVehicleScouts(root))));
    return;
  }
  const grid = h('div', { class: 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4' });
  opps.forEach((o) => grid.appendChild(opportunityCard(o, root)));
  container.appendChild(grid);
}

function opportunityCard(o, root) {
  const score = o.opportunityScore || 0;
  const scoreColor = score >= 85 ? 'red' : score >= 70 ? 'gold' : 'blue';
  return h('div', {
    class: `relative bg-base-800/50 border ${score >= 85 ? 'border-red-400/40' : 'border-base-700'} rounded-xl p-5 hover:border-gold-400/40 transition cursor-pointer`,
    onclick: () => openOpportunityDetail(o, root),
  }, [
    h('div', { class: 'absolute top-3 right-3 flex items-center gap-2' }, [
      o.starred ? h('span', { class: 'text-gold-300 text-lg' }, '★') : null,
      h('div', {
        class: `w-12 h-12 rounded-full border-2 flex items-center justify-center font-bold text-lg ${score >= 85 ? 'border-red-400 text-red-300 bg-red-400/10' : score >= 70 ? 'border-gold-400 text-gold-300 bg-gold-400/10' : 'border-base-600 text-ink-300'}`,
      }, String(score)),
    ]),
    h('div', { class: 'mb-2 pr-16' }, [
      h('h4', { class: 'font-display font-semibold leading-tight' }, `${o.brand || ''} ${o.model || ''} ${o.version || ''}`.trim()),
      h('p', { class: 'text-xs text-ink-400 mt-0.5' }, [o.year, o.km ? `${o.km.toLocaleString('es-ES')} km` : null, o.fuel, o.city].filter(Boolean).join(' · ')),
    ]),
    h('div', { class: 'flex flex-wrap gap-1.5 mb-3' }, [
      pill(o.source || '—', 'blue'),
      pill(o.status || 'new', 'gold'),
      o.transmission ? pill(o.transmission, 'gray') : null,
    ]),
    h('div', { class: 'grid grid-cols-2 gap-2 text-xs mb-3' }, [
      o.listingPrice ? metric('Anuncio', formatEur(o.listingPrice), 'gold') : null,
      o.marketAvgPrice ? metric('Mercado', formatEur(o.marketAvgPrice)) : null,
      o.pctBelowMarket ? metric('Bajo mercado', `${o.pctBelowMarket.toFixed(1)}%`, 'green') : null,
      o.netMargin ? metric('Margen neto', formatEur(o.netMargin), 'green') : null,
    ].filter(Boolean)),
    o.rationale ? h('p', { class: 'text-xs text-ink-300 line-clamp-2 italic' }, `"${o.rationale.slice(0, 140)}"`) : null,
    h('div', { class: 'flex items-center justify-between mt-3 pt-3 border-t border-base-700/50' }, [
      h('span', { class: 'text-[10px] text-ink-400' }, o.scoutAgent ? `🤖 ${o.scoutAgent}` : ''),
      o.externalUrl ? h('a', {
        href: o.externalUrl, target: '_blank', rel: 'noopener noreferrer',
        class: 'text-xs text-gold-300 hover:underline',
        onclick: (e) => e.stopPropagation(),
      }, 'Ver anuncio →') : null,
    ]),
  ]);
}

function metric(label, value, color = '') {
  const colors = { gold: 'text-gold-300', green: 'text-emerald-300', red: 'text-red-300' };
  return h('div', { class: 'flex justify-between bg-base-900/40 rounded-md px-2 py-1' }, [
    h('span', { class: 'text-ink-400' }, label),
    h('span', { class: `font-semibold ${colors[color] || 'text-ink-100'}` }, value),
  ]);
}

function openOpportunityDetail(o, root) {
  openModal({
    title: `${o.brand} ${o.model}`,
    icon: '🚗',
    size: 'lg',
    fields: [
      { name: 'status', label: 'Estado', type: 'select', value: o.status, options: ['new','reviewing','contacted','inspected','negotiating','bought','sold','discarded'] },
      { name: 'userNotes', label: 'Mis notas', type: 'textarea', rows: 4, value: o.userNotes },
    ],
    onSubmit: async (vals) => {
      await apiClient.patch(`/api/v1/autosell/opportunities/${o.id}`, vals);
      toast('Oportunidad actualizada', 'success');
      renderAutosell(root);
    },
  });
}

async function runVehicleScouts(root) {
  toast('🤖 Scouts vehículos analizando Coches.net, Wallapop, Milanuncios, AutoScout24...', 'info', 5000);
  try {
    await apiClient.post('/api/v1/ai/dispatch', {
      mode: 'panel',
      panel: ['scout-coches-net', 'scout-wallapop-vehiculos', 'scout-milanuncios-vehiculos', 'scout-autoscout24', 'scout-mobile-de', 'analista-oportunidades-final'],
      input: 'Busca chollos de vehículos en España y Alemania con margen ≥ 1500€. Score >= 80. Reportar con análisis ROI y riesgos. Inserta en /api/v1/autosell/opportunities.',
    }).catch(() => null);
  } catch {}
  setTimeout(() => { _autoTab = 'opportunities'; renderAutosell(root); }, 1500);
}

function vehCard(v, root) {
  return h('div', {
    class: 'bg-base-800 border border-base-700 rounded-lg p-3 mb-2 hover:border-gold-400/40 cursor-pointer transition group',
    onclick: () => openVehicleModal(v, () => renderAutosell(root)),
  }, [
    h('div', { class: 'flex items-start justify-between' }, [
      h('div', { class: 'font-semibold text-sm leading-tight' }, `${v.brand} ${v.model}`),
      h('button', {
        class: 'text-xs text-red-400 opacity-0 group-hover:opacity-100 transition',
        onclick: async (e) => {
          e.stopPropagation();
          const ok = await openConfirm({ title: 'Eliminar vehículo', message: `${v.brand} ${v.model}`, danger: true, icon: '🗑️' });
          if (!ok) return;
          await apiClient.delete(`/api/v1/vehicles/${v.id}`);
          toast('Vehículo eliminado', 'success');
          renderAutosell(root);
        },
      }, '🗑'),
    ]),
    h('div', { class: 'text-xs text-ink-300 mt-0.5' }, [
      v.year ? `${v.year}` : null,
      v.km ? `${v.km.toLocaleString('es-ES')} km` : null,
    ].filter(Boolean).join(' · ') || '—'),
    v.askingPrice ? h('div', { class: 'text-sm text-gold-300 font-semibold mt-1' }, formatEur(v.askingPrice)) : null,
  ]);
}

function openVehicleModal(vehicle, onDone) {
  openModal({
    title: vehicle ? 'Editar vehículo' : 'Nuevo vehículo',
    icon: '🚗',
    size: 'lg',
    fields: [
      { name: 'brand', label: 'Marca', required: true, value: vehicle?.brand, placeholder: 'BMW' },
      { name: 'model', label: 'Modelo', required: true, value: vehicle?.model, placeholder: 'Serie 3 320d' },
      { name: 'year', label: 'Año', type: 'number', value: vehicle?.year },
      { name: 'km', label: 'Kilómetros', type: 'number', value: vehicle?.km },
      { name: 'plate', label: 'Matrícula', value: vehicle?.plate, placeholder: '1234-ABC' },
      { name: 'status', label: 'Estado', type: 'select', value: vehicle?.status || 'sourcing', options: [
        { value: 'sourcing', label: 'Sourcing' },
        { value: 'inspecting', label: 'Inspección' },
        { value: 'available', label: 'Disponible' },
        { value: 'reserved', label: 'Reservado' },
        { value: 'sold', label: 'Vendido' },
        { value: 'withdrawn', label: 'Retirado' },
      ] },
      { name: 'purchasePrice', label: 'Precio compra (€)', type: 'number', value: vehicle?.purchasePrice },
      { name: 'askingPrice', label: 'Precio venta (€)', type: 'number', value: vehicle?.askingPrice },
      { name: 'notes', label: 'Notas', type: 'textarea', rows: 3, value: vehicle?.notes },
    ],
    onSubmit: async (vals) => {
      const payload = {
        ...vals,
        year: vals.year ? Number(vals.year) : null,
        km: vals.km ? Number(vals.km) : null,
        purchasePrice: vals.purchasePrice ? Number(vals.purchasePrice) : null,
        askingPrice: vals.askingPrice ? Number(vals.askingPrice) : null,
      };
      if (vehicle?.id) {
        await apiClient.patch(`/api/v1/vehicles/${vehicle.id}`, payload);
        toast('Vehículo actualizado', 'success');
      } else {
        await apiClient.post('/api/v1/vehicles', payload);
        toast('Vehículo añadido', 'success');
      }
      onDone();
    },
  });
}
